import { useStableCallback } from "@bezoned/shared/hooks/use-stable-callback.js";
import type { TeamMember } from "@bezoned/shared/types/api/team/team-member-list.types.js";
import { useCallback, useState } from "react";

import { BezonedConfirmDialog } from "../../../../components/ui/common/bezoned-confirm-dialog.js";
import { InviteModalEnterpriseVariant } from "../../../../components/ui/modals/variants/invite-modal-enterprise-variant.jsx";
import { TeamAddMembersDialog } from "./components/team-add-members-dialog.js";
import type { OrganizationUser } from "./components/team-add-members-dialog.js";
import { TeamMembersPagination } from "./components/team-members-pagination.js";
import {
  TeamMembersTable,
  useTeamMembersTable,
} from "./components/team-members-table.js";
import { TeamMembersToolbar } from "./components/team-members-toolbar.js";
import { DEFAULT_PAGE, PAGE_SIZE } from "./constants.js";
import {
  usePageState,
  useTeamMembersPagination,
} from "./hooks/use-team-members-pagination.js";
import { useTeamMembersSearch } from "./hooks/use-team-members-search.js";

interface TeamMembersTabProps {
  currentUserId: string | null;
  members: TeamMember[];
  isLoading: boolean;
  isSaving: boolean;
  errorMessage: string | null;
  onAddUsers: (users: OrganizationUser[]) => void;
  onAddAllTeamsUsers?: () => void;
  allowAddAllTeamsUsersAction?: boolean;
  onRemoveUser: (member: TeamMember) => void;
  persistPaginationInQuery?: boolean;
  isEnterprise?: boolean;
  channelId?: string | undefined;
  teamId?: string;
  tenantId?: string;
}

const useLocalPageState = () => {
  const [page, setPage] = useState(DEFAULT_PAGE);

  const resetPagination = useCallback(() => {
    setPage(DEFAULT_PAGE);
  }, []);

  return {
    page,
    pageIndex: Math.max(page - 1, 0),
    resetPagination,
    setPage,
  };
};

export const TeamMembersTab = ({
  currentUserId,
  members,
  isLoading,
  isSaving,
  errorMessage,
  onAddUsers,
  onAddAllTeamsUsers,
  allowAddAllTeamsUsersAction = false,
  onRemoveUser,
  persistPaginationInQuery = true,
  isEnterprise = false,
  channelId,
  teamId,
  tenantId,
}: TeamMembersTabProps) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [enterpriseModalOpen, setEnterpriseModalOpen] = useState(false);
  const [pendingMember, setPendingMember] = useState<TeamMember | null>(null);

  const queryPageState = usePageState();
  const localPageState = useLocalPageState();
  const pageState = persistPaginationInQuery ? queryPageState : localPageState;
  const search = useTeamMembersSearch({
    resetPagination: pageState.resetPagination,
  });

  const table = useTeamMembersTable({
    data: members,
    isRemovePending: isSaving,
    onRemoveUser: setPendingMember,
    pageIndex: pageState.pageIndex,
    pageSize: PAGE_SIZE,
    searchTerm: search.searchTerm,
  });

  const pagination = useTeamMembersPagination(table, pageState);

  const totalUsers = table.getPrePaginationRowModel().rows.length;
  const shouldShowPagination = totalUsers > PAGE_SIZE;
  const isTableLoading = members.length === 0 && isLoading;
  const excludedUserIds = new Set(members.map((user) => user.id));
  const handleConfirm138 = useStableCallback(() => {
    if (!pendingMember) {
      return;
    }

    onRemoveUser(pendingMember);
  });

  const handleCancel137 = useStableCallback(() => setPendingMember(null));

  const handleClose123 = useStableCallback(() => setAddDialogOpen(false));
  const handleCloseEnterpriseModal = useStableCallback(() =>
    setEnterpriseModalOpen(false)
  );

  const handlePreviousPage114 = useStableCallback(() =>
    pagination.setPage(pagination.page - 1)
  );

  const handleNextPage113 = useStableCallback(() =>
    pagination.setPage(pagination.page + 1)
  );

  const handleAddClick92 = useStableCallback(() => {
    if (isEnterprise) {
      setEnterpriseModalOpen(true);
      return;
    }

    setAddDialogOpen(true);
  });
  const handleSearchChange115 = useStableCallback((value) => {
    search.setSearchInput(value);
    search.debouncedSearch(value);
  });

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex flex-col gap-4">
        <TeamMembersToolbar
          isSearchDisabled={isLoading || isSaving}
          onAddClick={handleAddClick92}
          onSearchChange={handleSearchChange115}
          searchInput={search.searchInput}
        />

        <TeamMembersTable
          currentUserId={currentUserId}
          errorMessage={errorMessage}
          isLoading={isTableLoading}
          table={table}
        />

        {shouldShowPagination && (
          <TeamMembersPagination
            currentPage={pagination.page}
            hasNextPage={pagination.hasNextPage}
            hasPreviousPage={pagination.hasPreviousPage}
            isDisabled={isLoading || isSaving}
            onNextPage={handleNextPage113}
            onPreviousPage={handlePreviousPage114}
          />
        )}
      </div>

      <TeamAddMembersDialog
        allowAddAllTeamsUsersAction={allowAddAllTeamsUsersAction}
        excludedUserIds={excludedUserIds}
        onAddUsers={onAddUsers}
        onClose={handleClose123}
        open={addDialogOpen}
        {...(onAddAllTeamsUsers ? { onAddAllTeamsUsers } : {})}
      />

      {isEnterprise && teamId && tenantId && enterpriseModalOpen ? (
        <InviteModalEnterpriseVariant
          channelId={channelId}
          onClose={handleCloseEnterpriseModal}
          teamId={teamId}
          tenantId={tenantId}
          title="Add member"
        />
      ) : null}

      <BezonedConfirmDialog
        confirmLabel="Remove"
        content={
          <span>
            Are you sure you want to remove{" "}
            <strong>{pendingMember?.displayName ?? "this member"}</strong>?
          </span>
        }
        isConfirming={isSaving}
        onCancel={handleCancel137}
        onConfirm={handleConfirm138}
        open={pendingMember !== null}
        title="Remove member"
      />
    </div>
  );
};
